import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router";
import { useTextDomainContext, useUser } from "@/app/providers";
import { useQuery, useMutation } from "@apollo/client/react";
import { queries, mutations } from "@/graphql";
import { enqueueSnackbar } from "notistack";
import { usePageTitle, useTenantList } from "@/hooks";

const CHART_OPTIONS = ["Default", "Bar Chart", "Line Chart", "Area Chart"];

const getSettingItems = (gettext: (s: string) => string) => [
  {
    key: "energy-chart-type",
    label: gettext("Energy Chart Type"),
    description: gettext(
      "Select the corporate group chart visualization type for energy consumption data.",
    ),
  },
  {
    key: "climate-chart-type",
    label: gettext("Climate Chart Type"),
    description: gettext(
      "Select the corporate group chart visualization type for climate and environmental data.",
    ),
  },
  {
    key: "cost-chart-type",
    label: gettext("Cost Chart Type"),
    description: gettext(
      "Select the corporate group chart visualization type for cost and financial analytics.",
    ),
  },
  {
    key: "budget-chart-type",
    label: gettext("Budget Chart Type"),
    description: gettext(
      "Select the corporate group chart visualization type for budget forecasting and analytics.",
    ),
  },
];

export const CorporateSettings = ({
  tenantId: propTenantId,
}: {
  tenantId?: string;
} = {}) => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const { user } = useUser();
  const navigate = useNavigate();
  const params = useParams<{ tenantId?: string }>();
  const tenantIdFromRoute = propTenantId || params.tenantId;

  usePageTitle(gettext("Corporate Group Settings"));

  const effectiveTenantId =
    tenantIdFromRoute || user?.currentTenantId || user?.tenant?.id;

  const { tenantsList } = useTenantList({
    tenantId: effectiveTenantId || undefined,
    ignoreRole: true,
  });
  const currentTenant = tenantsList?.find(
    (t) => t?.Id === effectiveTenantId || (t as any)?.id === effectiveTenantId,
  );
  const effectiveTenantName = currentTenant?.name;

  const settingItems = getSettingItems(gettext);

  const [settingsValues, setSettingsValues] = useState<Record<string, string>>({
    "energy-chart-type": "Default",
    "climate-chart-type": "Default",
    "cost-chart-type": "Default",
    "budget-chart-type": "Default",
  });

  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [saveAllLoading, setSaveAllLoading] = useState<boolean>(false);

  // Queries to fetch initial settings from tenant_settings
  const { data: energyData, loading: energyLoading } = useQuery<{
    tenantSettings: any;
  }>(queries.tenantSettings, {
    variables: {
      tenantId: effectiveTenantId || undefined,
      key: "energy-chart-type",
    },
    skip: !effectiveTenantId,
    fetchPolicy: "network-only",
  });

  const { data: climateData, loading: climateLoading } = useQuery<{
    tenantSettings: any;
  }>(queries.tenantSettings, {
    variables: {
      tenantId: effectiveTenantId || undefined,
      key: "climate-chart-type",
    },
    skip: !effectiveTenantId,
    fetchPolicy: "network-only",
  });

  const { data: costData, loading: costLoading } = useQuery<{
    tenantSettings: any;
  }>(queries.tenantSettings, {
    variables: {
      tenantId: effectiveTenantId || undefined,
      key: "cost-chart-type",
    },
    skip: !effectiveTenantId,
    fetchPolicy: "network-only",
  });

  const { data: budgetData, loading: budgetLoading } = useQuery<{
    tenantSettings: any;
  }>(queries.tenantSettings, {
    variables: {
      tenantId: effectiveTenantId || undefined,
      key: "budget-chart-type",
    },
    skip: !effectiveTenantId,
    fetchPolicy: "network-only",
  });

  const parseVal = (raw: any): string => {
    if (!raw) return "Default";
    const parsed =
      typeof raw === "object" && raw !== null && "tenantSettings" in raw
        ? raw.tenantSettings
        : raw;
    return typeof parsed === "string" && CHART_OPTIONS.includes(parsed)
      ? parsed
      : "Default";
  };

  useEffect(() => {
    setSettingsValues((prev) => ({
      ...prev,
      "energy-chart-type": parseVal(energyData),
    }));
  }, [energyData, effectiveTenantId]);

  useEffect(() => {
    setSettingsValues((prev) => ({
      ...prev,
      "climate-chart-type": parseVal(climateData),
    }));
  }, [climateData, effectiveTenantId]);

  useEffect(() => {
    setSettingsValues((prev) => ({
      ...prev,
      "cost-chart-type": parseVal(costData),
    }));
  }, [costData, effectiveTenantId]);

  useEffect(() => {
    setSettingsValues((prev) => ({
      ...prev,
      "budget-chart-type": parseVal(budgetData),
    }));
  }, [budgetData, effectiveTenantId]);

  const [updateTenantSettings] = useMutation<any>(
    mutations.updateTenantSettings,
    {
      errorPolicy: "all",
    },
  );

  const handleValueChange = (key: string, val: string) => {
    setSettingsValues((prev) => ({
      ...prev,
      [key]: val,
    }));
  };

  const saveSingleSetting = async (
    key: string,
    label: string,
    valOverride?: string,
  ) => {
    const val = valOverride ?? settingsValues[key] ?? "Default";
    setSavingKey(key);
    try {
      const res: any = await updateTenantSettings({
        variables: {
          tenantId: effectiveTenantId || undefined,
          key,
          data: JSON.stringify(val),
        },
        refetchQueries: ["TenantSettings"],
      });
      const result = res.data?.updateTenantSettings;
      if (result?.success) {
        enqueueSnackbar(gettext("Setting updated successfully!"), {
          variant: "success",
        });
      } else {
        const errorMsg =
          result?.message?.[0] ||
          res.errors?.[0]?.message ||
          gettext("Failed to save setting.");
        enqueueSnackbar(errorMsg, { variant: "error" });
      }
    } catch (err: any) {
      enqueueSnackbar(err.message || gettext("An error occurred"), {
        variant: "error",
      });
    } finally {
      setSavingKey(null);
    }
  };

  const handleSaveAll = async () => {
    setSaveAllLoading(true);
    try {
      for (let i = 0; i < settingItems.length; i++) {
        const item = settingItems[i];
        const val = settingsValues[item.key] || "Default";
        const isLast = i === settingItems.length - 1;
        await updateTenantSettings({
          variables: {
            tenantId: effectiveTenantId || undefined,
            key: item.key,
            data: JSON.stringify(val),
          },
          ...(isLast ? { refetchQueries: ["TenantSettings"] } : {}),
        });
      }
      enqueueSnackbar(
        gettext("All corporate group settings saved successfully!"),
        {
          variant: "success",
        },
      );
    } catch (err: any) {
      enqueueSnackbar(err.message || gettext("Failed to save settings"), {
        variant: "error",
      });
    } finally {
      setSaveAllLoading(false);
    }
  };

  const isInitialLoading =
    energyLoading || climateLoading || costLoading || budgetLoading;

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            {gettext("Corporate Group Settings")}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {effectiveTenantName
              ? `${gettext("Configure chart visualization preferences for")} ${effectiveTenantName}.`
              : gettext(
                  "Configure chart visualization preferences for your corporate group.",
                )}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          {tenantIdFromRoute && (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
            >
              {gettext("Back")}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={
              saveAllLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            onClick={handleSaveAll}
            disabled={saveAllLoading || isInitialLoading}
          >
            {gettext("Save All Settings")}
          </Button>
        </Stack>
      </Box>

      {isInitialLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {settingItems.map((item) => (
            <Grid size={{ xs: 12, md: 4 }} key={item.key}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mb: 3 }}
                  >
                    {item.description}
                  </Typography>

                  <FormControl fullWidth sx={{ mt: 1 }}>
                    <InputLabel id={`select-tenant-label-${item.key}`}>
                      {item.label}
                    </InputLabel>
                    <Select
                      labelId={`select-tenant-label-${item.key}`}
                      value={settingsValues[item.key] || "Default"}
                      label={item.label}
                      onChange={(e) =>
                        handleValueChange(item.key, e.target.value)
                      }
                    >
                      {CHART_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {gettext(opt)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </CardContent>
                <Box
                  sx={{
                    p: 2,
                    pt: 0,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={savingKey === item.key}
                    onClick={() => saveSingleSetting(item.key, item.label)}
                  >
                    {savingKey === item.key ? (
                      <CircularProgress size={16} />
                    ) : (
                      gettext("Save")
                    )}
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};
